import React from 'react';

import { useLogger } from '@hooks';

import { debugSettingsContext } from '../debugSettings';

export type Type = 'displayGroup' | 'placeholder' | 'component';

enum ComponentInfoColor {
	DisplayGroup = 'green',
	Placeholder = 'red',
	Component = 'blue',
	Default = 'gray',
}

type AsProp<C extends React.ElementType> = {
	as?: C;
};

interface OtherProps {
	name: string;
	type?: Type;
}

type ComponentInfoProps<C extends React.ElementType> = OtherProps &
	React.PropsWithChildren<AsProp<C>> &
	React.ComponentPropsWithoutRef<C>;

/**
 * It's a Component to Debug. Use it in DisplayGroup and Placeholders.
 * Avoid using in Components, can be used just for debug, when finish debug, delete ComponentInfo to your Component as it can add more complexity.
 *
 * @param name 		Required field. Name of Functional Component to Debug.
 * @param type 		Type of Functional Component to Debug. Insert correct type: 'displayGroup' | 'placeholder' | 'component'.
 * @param as 		Can add Components inside this prop. Ex: as={Box} or as="div".
 * @param children 		Required field. It's React.ReactNode.
 * @returns			Component to debug and can be used the props of the Component that was inserted in the as.
 */
export const ComponentInfo = <C extends React.ElementType>({
	name,
	type,
	as,
	children,
	...restProps
}: ComponentInfoProps<C>) => {
	const { componentInfo } = React.useContext(debugSettingsContext);
	const logger = useLogger();

	logger('ComponentInfo', { componentInfo });

	logger('ComponentInfo > Supplied Component', { as });

	const color = React.useMemo(() => {
		if (type === 'displayGroup') {
			return ComponentInfoColor.DisplayGroup;
		} else if (type === 'placeholder') {
			return ComponentInfoColor.Placeholder;
		} else if (type === 'component') {
			return ComponentInfoColor.Component;
		} else {
			return ComponentInfoColor.Default;
		}
	}, [type]);

	const Component = as || React.Fragment;

	if (!componentInfo) {
		return <Component {...restProps}>{children}</Component>;
	}

	return (
		<Component
			style={{
				border: `1px solid ${color}`,
				padding: '1px',
				color: color,
			}}
			{...restProps}
		>
			{name}
			{children}
		</Component>
	);
};
